import React, { useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Target, Calculator, CreditCard, Shield, LogOut, BookOpen, Receipt, ChevronDown, ChevronRight, Package, Settings, Users, Layers, FolderTree, UserCheck, Banknote, BarChart3, Truck, Video, Repeat, Percent, Construction, CalendarClock, TrendingUp, Wallet, PieChart, GraduationCap, PlayCircle, HardHat, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotificacoesPanel from '@/components/NotificacoesPanel';
import { PassoAPassoContext } from '../App';

export const Sidebar = ({ user, onLogout, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [financeiroMenuOpen, setFinanceiroMenuOpen] = useState(false);
  const [contasSubMenuOpen, setContasSubMenuOpen] = useState(false);
  const [orcamentosMenuOpen, setOrcamentosMenuOpen] = useState(false);
  const [clientesMenuOpen, setClientesMenuOpen] = useState(false);
  const [notasMenuOpen, setNotasMenuOpen] = useState(false);
  const company = JSON.parse(localStorage.getItem('company') || '{}');
  const permissoes = JSON.parse(localStorage.getItem('permissoes') || '{}');
  
  // Verificar se é funcionário (role FUNCIONARIO ou legacy 'funcionario')
  const isFuncionario = user?.role === 'FUNCIONARIO' || user?.role === 'funcionario';
  const isFuncionarioSis = user?.categoria === 'FuncionárioSis';
  const permissoesUsuario = user?.permissoes || {};
  
  // Mapeamento de path -> módulo de permissão
  const PATH_TO_MODULE = {
    '/lancamentos': 'lancamentos',
    '/contas-pagar': 'contas_pagar',
    '/novo-boleto': 'contas_pagar',
    '/contas-receber': 'contas_receber',
    '/custos-fixos': 'custos_fixos',
    '/custos-variaveis': 'custos_variaveis',
    '/meta-mensal': 'meta_mensal',
    '/plano-contas': 'plano_contas',
    '/dfc': 'dfc',
    '/dre': 'dre',
    '/relatorios': 'relatorios',
    '/clientes': 'clientes',
    '/fornecedores': 'fornecedores',
    '/funcionarios': 'funcionarios',
    '/empresa': 'empresa',
    '/assinatura': 'assinatura',
  };
  
  const hasPermission = (path) => {
    if (!isFuncionario) return true;
    const mod = PATH_TO_MODULE[path];
    if (!mod) return true; // dashboard, educa, passo-a-passo são livres
    const level = permissoesUsuario[mod];
    return level === 'read' || level === 'write';
  };
  
  // Contexto para abrir o Passo a Passo
  const passoAPassoContext = useContext(PassoAPassoContext);

  // Função para navegar (usa onNavigate se disponível, senão usa navigate diretamente)
  const handleNavigation = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Painel Geral' },
    { 
      type: 'submenu',
      menuKey: 'financeiro',
      icon: Wallet, 
      label: 'Financeiro',
      items: [
        { path: '/lancamentos', label: 'Lançamentos', icon: FileText },
        { 
          type: 'nested-submenu',
          menuKey: 'contas',
          icon: Receipt, 
          label: 'Contas',
          items: [
            { path: '/contas-pagar', label: 'A Pagar' },
            { path: '/novo-boleto', label: 'Novo Boleto', icon: ScanLine },
            { path: '/contas-receber', label: 'A Receber' },
            { path: '/custos-fixos', label: 'Despesas Recorrentes', icon: Repeat },
            { path: '/custos-variaveis', label: 'Margem de Contribuição', icon: Percent },
          ]
        },
        { path: '/dfc', label: 'DFC', icon: Banknote },
        { path: '/dre', label: 'DRE', icon: PieChart },
        { path: '/meta-mensal', label: 'Meta Mensal', icon: Target },
        { path: '/plano-contas', label: 'Plano de Contas', icon: FolderTree },
      ]
    },
    { path: '/relatorios', icon: BarChart3, label: 'Relatórios' },
    { path: '/clientes', icon: Users, label: 'Clientes' },
    { path: '/fornecedores', icon: Truck, label: 'Fornecedores' },
    { path: '/funcionarios', icon: UserCheck, label: 'Funcionários' },
    { path: '/contadores', icon: Calculator, label: 'Contadores' },
    {
      type: 'submenu',
      menuKey: 'notas',
      icon: Package,
      label: 'Fiscais',
      items: [
        { path: '/conferencia-mensal', label: 'Conferência Mensal' },
        { path: '/notas-fiscais', label: 'Notas de Compra' },
        { path: '/notas-venda', label: 'Notas de Venda' },
        { path: '/notas-servico', label: 'NFs Prestado' },
        { path: '/notas-tomadas', label: 'NFs Tomado' },
        { path: '/outros-documentos', label: 'Outros Documentos' },
      ]
    },
    { path: '/empresa', icon: Shield, label: 'Empresa' },
    { path: '/assinatura', icon: CreditCard, label: 'Assinatura' },
    { path: '/educa-empresario', icon: GraduationCap, label: 'Educa Empresário' },
    { type: 'action', action: 'passo-a-passo', icon: PlayCircle, label: 'Passo a Passo' },
  ];

  // Filtrar menu items baseado nas permissões granulares do funcionário (Phase 5)
  const getFilteredMenuItems = () => {
    if (!isFuncionario) return menuItems;

    return menuItems
      .map((item) => {
        // Notas Fiscais: acesso restrito a GESTOR/ADMIN, sem módulo de permissão (mesma regra de Contadores)
        if (item.type === 'submenu' && item.menuKey === 'notas' && isFuncionario) return null;
        // Submenu: filtra subitens
        if (item.type === 'submenu') {
          const filteredChildren = (item.items || []).map((child) => {
            if (child.type === 'nested-submenu') {
              const grand = (child.items || []).filter((g) => hasPermission(g.path));
              return grand.length ? { ...child, items: grand } : null;
            }
            return hasPermission(child.path) ? child : null;
          }).filter(Boolean);
          return filteredChildren.length ? { ...item, items: filteredChildren } : null;
        }
        // Item simples
        if (item.path) {
          if (item.path === '/educa-empresario' && isFuncionarioSis) return null;
          // Contadores: acesso restrito a GESTOR/ADMIN, sem módulo de permissão
          if (item.path === '/contadores' && isFuncionario) return null;
          return hasPermission(item.path) ? item : null;
        }
        // Action (passo-a-passo)
        if (item.type === 'action' && isFuncionarioSis) return null;
        return item;
      })
      .filter(Boolean);
  };
  
  const filteredMenuItems = getFilteredMenuItems();

  // Adicionar items admin se usuário for admin
  if (user?.role === 'ADMIN' || user?.role === 'admin') {
    filteredMenuItems.push({ path: '/admin', icon: Shield, label: 'Admin' });
  }

  // Verificar se algum item do financeiro está ativo
  const financeiroItems = ['/lancamentos', '/contas-pagar', '/novo-boleto', '/contas-receber', '/custos-fixos', '/custos-variaveis', '/dfc', '/dre', '/meta-mensal', '/plano-contas'];
  const isFinanceiroActive = financeiroItems.includes(location.pathname);
  const isContasActive = ['/contas-pagar', '/novo-boleto', '/contas-receber', '/custos-fixos', '/custos-variaveis'].includes(location.pathname);
  
  // (clientes/orçamentos submenus removidos no SegCore)
  const isClientesActive = false;
  const isOrcamentosActive = false;
  const isNotasActive = ['/conferencia-mensal', '/notas-fiscais', '/notas-venda', '/notas-servico', '/notas-tomadas', '/outros-documentos'].includes(location.pathname);

  return (
    <div className="w-64 min-h-screen glass border-r border-white/10 flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold gradient-text" data-testid="sidebar-logo">
            SegCore
          </h1>
          {/* Painel de Notificações */}
          <NotificacoesPanel companyId={company?.id} userId={user?.id} />
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex-1">
            <p className="text-sm text-gray-400 mt-1" data-testid="sidebar-subtitle">Gestão Financeira</p>
          </div>
        </div>
        
        {/* User Info inline */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex-1" data-testid="sidebar-user-info">
            <p className="text-xs font-medium text-white">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
            {isFuncionario && (
              <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                FUNCIONÁRIO
              </span>
            )}
          </div>
          <button
            onClick={onLogout}
            className="p-2 rounded-lg border border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all"
            title="Sair do sistema"
            data-testid="sidebar-logout-button"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Menu */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {filteredMenuItems.map((item, index) => {
          // Se for submenu Financeiro
          if (item.type === 'submenu' && item.menuKey === 'financeiro') {
            const Icon = item.icon;
            
            return (
              <div key={`submenu-financeiro`}>
                <button
                  onClick={() => setFinanceiroMenuOpen(!financeiroMenuOpen)}
                  className={`w-full flex items-center justify-between space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isFinanceiroActive
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid="sidebar-financeiro-menu"
                >
                  <div className="flex items-center space-x-3">
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {financeiroMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                
                {financeiroMenuOpen && (
                  <div className="mt-2 ml-4 space-y-1">
                    {item.items.map((subItem) => {
                      // Se for submenu aninhado (Contas)
                      if (subItem.type === 'nested-submenu') {
                        const SubIcon = subItem.icon;
                        return (
                          <div key={`nested-${subItem.menuKey}`}>
                            <button
                              onClick={() => setContasSubMenuOpen(!contasSubMenuOpen)}
                              className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-all ${
                                isContasActive
                                  ? 'bg-white/10 text-white font-medium'
                                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {SubIcon && <SubIcon size={14} />}
                                {subItem.label}
                              </div>
                              {contasSubMenuOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                            
                            {contasSubMenuOpen && (
                              <div className="mt-1 ml-6 space-y-1">
                                {subItem.items.map((nestedItem) => {
                                  const isNestedActive = location.pathname === nestedItem.path;
                                  const NestedIcon = nestedItem.icon;
                                  return (
                                    <div
                                      key={nestedItem.path}
                                      onClick={() => handleNavigation(nestedItem.path)}
                                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all cursor-pointer ${
                                        isNestedActive
                                          ? 'bg-white/10 text-white font-medium'
                                          : 'text-gray-500 hover:bg-white/5 hover:text-white'
                                      }`}
                                    >
                                      {NestedIcon && <NestedIcon size={12} />}
                                      {nestedItem.label}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      // Item normal do submenu financeiro
                      const isSubActive = location.pathname === subItem.path;
                      const SubIcon = subItem.icon;
                      return (
                        <div
                          key={subItem.path}
                          onClick={() => handleNavigation(subItem.path)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                            isSubActive
                              ? 'bg-white/10 text-white font-medium'
                              : 'text-gray-400 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          {SubIcon && <SubIcon size={14} />}
                          {subItem.label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Se for submenu Orçamentos
          if (item.type === 'submenu' && item.menuKey === 'orcamentos') {
            const Icon = item.icon;
            const isAnySubActive = item.items.some(subItem => location.pathname === subItem.path);
            
            return (
              <div key={`submenu-orcamentos`}>
                <button
                  onClick={() => setOrcamentosMenuOpen(!orcamentosMenuOpen)}
                  className={`w-full flex items-center justify-between space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isAnySubActive
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid="sidebar-orcamentos-menu"
                >
                  <div className="flex items-center space-x-3">
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {orcamentosMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                
                {orcamentosMenuOpen && (
                  <div className="mt-2 ml-8 space-y-1">
                    {item.items.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path;
                      const SubIcon = subItem.icon;
                      return (
                        <div
                          key={subItem.path}
                          onClick={() => handleNavigation(subItem.path)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                            isSubActive
                              ? 'bg-white/10 text-white font-medium'
                              : 'text-gray-400 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          {SubIcon && <SubIcon size={14} />}
                          {subItem.label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Se for submenu Clientes
          if (item.type === 'submenu' && item.menuKey === 'clientes') {
            const Icon = item.icon;
            
            return (
              <div key={`submenu-clientes`}>
                <button
                  onClick={() => setClientesMenuOpen(!clientesMenuOpen)}
                  className={`w-full flex items-center justify-between space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isClientesActive
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid="sidebar-clientes-menu"
                >
                  <div className="flex items-center space-x-3">
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {clientesMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                
                {clientesMenuOpen && (
                  <div className="mt-2 ml-8 space-y-1">
                    {item.items.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path;
                      const SubIcon = subItem.icon;
                      return (
                        <div
                          key={subItem.path}
                          onClick={() => handleNavigation(subItem.path)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                            isSubActive
                              ? 'bg-white/10 text-white font-medium'
                              : 'text-gray-400 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          {SubIcon && <SubIcon size={14} />}
                          {subItem.label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Se for submenu Notas Fiscais
          if (item.type === 'submenu' && item.menuKey === 'notas') {
            const Icon = item.icon;

            return (
              <div key={`submenu-notas`}>
                <button
                  onClick={() => setNotasMenuOpen(!notasMenuOpen)}
                  className={`w-full flex items-center justify-between space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isNotasActive
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid="sidebar-notas-menu"
                >
                  <div className="flex items-center space-x-3">
                    <Icon size={20} />
                    <span className="font-medium">{item.label}</span>
                  </div>
                  {notasMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>

                {notasMenuOpen && (
                  <div className="mt-2 ml-8 space-y-1">
                    {item.items.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path;
                      const SubIcon = subItem.icon;
                      return (
                        <div
                          key={subItem.path}
                          onClick={() => handleNavigation(subItem.path)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                            isSubActive
                              ? 'bg-white/10 text-white font-medium'
                              : 'text-gray-400 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          {SubIcon && <SubIcon size={14} />}
                          {subItem.label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Se for uma ação (como abrir popup Passo a Passo)
          if (item.type === 'action') {
            const Icon = item.icon;
            
            const handleAction = () => {
              if (item.action === 'passo-a-passo' && passoAPassoContext) {
                passoAPassoContext.openPassoAPasso();
              }
            };
            
            return (
              <div 
                key={item.action} 
                onClick={handleAction} 
                data-testid={`sidebar-action-${item.action}`}
                className="cursor-pointer"
              >
                <div
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg transition-all text-gray-300 hover:bg-white/5 hover:text-white"
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </div>
              </div>
            );
          }
          
          // Menu normal
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <div 
              key={item.path} 
              onClick={() => handleNavigation(item.path)} 
              data-testid={`sidebar-link-${item.label.toLowerCase().replace(' ', '-')}`}
              className="cursor-pointer"
            >
              <div
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </div>
            </div>
          );
        })}
      </nav>

      {/* Admin Badge (se aplicável) */}
      {user?.role === 'admin' && (
        <div className="p-4 border-t border-white/10">
          <span className="inline-block px-3 py-1 text-xs bg-purple-600 rounded-full text-white" data-testid="sidebar-admin-badge">
            👑 Administrador
          </span>
        </div>
      )}
    </div>
  );
};